/* =========================================================
   Deli Delé — Homepage interactions
   Visual language inspired by aicm.store
   Tabs (left rail) × Flavors (dropdown) × Menu overlay
   ========================================================= */
(function () {
  'use strict';

  const site = window.DELI_DELE_SITE || {};
  const PRODUCTS = Array.isArray(site.products) ? site.products : [];
  const strings = site.strings || {};

  /* ---------- State ---------- */
  const state = {
    productId: PRODUCTS[0]?.id || '',
    tab: 'nutrition'
  };

  const getProduct = () => PRODUCTS.find(p => p.id === state.productId) || PRODUCTS[0] || {};

  /* ---------- DOM refs ---------- */
  const productImg     = document.querySelector('[data-product-img]');
  const panel          = document.querySelector('[data-panel]');
  const panelShell     = panel?.closest('.home__panel');
  const panelLegend    = document.querySelector('[data-panel-legend]');
  const tabButtons     = Array.from(document.querySelectorAll('.home__tab'));
  const flavor         = document.querySelector('[data-flavor]');
  const flavorToggle   = document.querySelector('[data-flavor-toggle]');
  const flavorList     = document.querySelector('[data-flavor-list]');
  const flavorLabel    = document.querySelector('[data-flavor-label]');
  const nodeText       = document.querySelector('[data-node-text]');
  const menuToggle     = document.querySelector('[data-menu-toggle]');
  const menuOverlay    = document.querySelector('[data-menu-overlay]');
  const menuClose      = document.querySelector('[data-menu-close]');
  const particleHost   = document.querySelector('[data-particles]');
  // Keep keyboard focus within modal navigation while it is open.
  const menuFocusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let lastMenuFocus = null;
  const tabNodeLabels = Object.fromEntries(tabButtons.map(button => [
    button.dataset.tab,
    button.querySelector('.home__tab-label')?.textContent?.trim() || ''
  ]));

  const ORBIT_PATH = {
    width: 220,
    height: 560,
    start: { x: 18, y: 24 },
    control: { x: 120, y: 280 },
    end: { x: 18, y: 536 }
  };
  const NUTRITION_ORBIT_STOPS = [0.04, 0.2, 0.36, 0.52, 0.68, 0.84, 0.96];

  function orbitStopsForCount(count) {
    if (count <= 1) return [0.5];
    const start = 0.04;
    const end = 0.96;
    return Array.from({ length: count }, (_, index) => start + ((end - start) * index / (count - 1)));
  }

  function pointOnOrbit(t) {
    const inv = 1 - t;
    const x = inv * inv * ORBIT_PATH.start.x
      + 2 * inv * t * ORBIT_PATH.control.x
      + t * t * ORBIT_PATH.end.x;
    const y = inv * inv * ORBIT_PATH.start.y
      + 2 * inv * t * ORBIT_PATH.control.y
      + t * t * ORBIT_PATH.end.y;

    return {
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10
    };
  }

  function orbitStyle(t) {
    const point = pointOnOrbit(t);
    const mobileY = Math.round((point.y / ORBIT_PATH.height) * 1000) / 10;
    return `--orbit-x:${point.x}px; --orbit-y:${point.y}px; --orbit-y-mobile:${mobileY}%;`;
  }

  function renderOrbitGuide() {
    return `
      <svg class="home__orbit-guide" viewBox="0 0 ${ORBIT_PATH.width} ${ORBIT_PATH.height}" preserveAspectRatio="none" aria-hidden="true">
        <path d="M ${ORBIT_PATH.start.x} ${ORBIT_PATH.start.y} Q ${ORBIT_PATH.control.x} ${ORBIT_PATH.control.y} ${ORBIT_PATH.end.x} ${ORBIT_PATH.end.y}"/>
      </svg>`;
  }

  /* ---------- Light sparkle particles ---------- */
  function buildParticles(count = 50) {
    if (!particleHost) return;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.style.top    = (Math.random() * 100).toFixed(2) + '%';
      dot.style.left   = (Math.random() * 100).toFixed(2) + '%';
      const size = (1 + Math.random() * 2).toFixed(1);
      dot.style.width  = size + 'px';
      dot.style.height = size + 'px';
      dot.style.animationDelay    = (Math.random() * 4).toFixed(2) + 's';
      dot.style.animationDuration = (3 + Math.random() * 4).toFixed(2) + 's';
      dot.style.opacity = (0.15 + Math.random() * 0.55).toFixed(2);
      frag.appendChild(dot);
    }
    particleHost.appendChild(frag);
  }

  /* ---------- Renderers ---------- */
  function renderFlavorList() {
    flavorList.innerHTML = PRODUCTS.map(p => `
      <li role="option">
        <button type="button" data-flavor-option="${p.id}" aria-selected="${p.id === state.productId}">
          ${p.name}
        </button>
      </li>
    `).join('');
  }

  function renderProduct() {
    const p = getProduct();
    if (!productImg) return;
    productImg.classList.add('is-swapping');
    setTimeout(() => {
      productImg.src = p.image || '';
      productImg.alt = String(strings.imageAlt || '').replaceAll('[product]', p.name || '');
      if (Number(p.imageWidth) > 0) productImg.width = Number(p.imageWidth);
      if (Number(p.imageHeight) > 0) productImg.height = Number(p.imageHeight);
      requestAnimationFrame(() => productImg.classList.remove('is-swapping'));
    }, 220);
  }

  function renderNutritionRow(row, index, stops = NUTRITION_ORBIT_STOPS) {
    const labelOnly = !row.value && !row.dv;
    return `
      <li class="home__nutri-item home__orbit-item ${labelOnly ? 'home__nutri-item--label' : ''}" style="${orbitStyle(stops[index] ?? 0.5)}">
        ${labelOnly ? '' : `<span class="home__nutri-name">${row.name}</span>`}
        <span class=" ${labelOnly ? 'home__nutri-name' : 'home__nutri-value'}">
          ${labelOnly ? row.name : row.value}
          ${row.dv ? `<span class="home__nutri-dv">${row.dv}</span>` : ''}
        </span>
      </li>`;
  }

  function renderMagicRow(value, index, stops) {
    return `
      <li class="home__magic-item home__orbit-item" style="${orbitStyle(stops[index] ?? 0.5)}">
        <span class="home__magic-value">${value}</span>
      </li>`;
  }

  function renderPanel() {
    const p = getProduct();
    if (!panel) return;
    panel.classList.add('is-swapping');
    if (panelShell) panelShell.dataset.mode = state.tab;

    setTimeout(() => {
      let html = '';
      if (!p.id) {
        panel.innerHTML = '';
        requestAnimationFrame(() => panel.classList.remove('is-swapping'));
        return;
      }
      if (state.tab === 'nutrition') {
        const nutritionStops = orbitStopsForCount(p.nutrition?.length || 0);
        html = `
          <div class="home__orbit home__orbit--nutrition">
            ${renderOrbitGuide()}
            <ul class="home__nutri-list home__orbit-list">
            ${(p.nutrition || []).map((row, index) => renderNutritionRow(row, index, nutritionStops)).join('')}
            </ul>
          </div>`;
        if (panelLegend) panelLegend.hidden = false;
      } else if (state.tab === 'magic') {
        const magicTitle = window.DELI_DELE_SITE?.magicInside?.title || p.magic?.title || '';
        const magicIngredients = (window.DELI_DELE_SITE?.magicInside?.ingredients && window.DELI_DELE_SITE.magicInside.ingredients.length > 0)
          ? window.DELI_DELE_SITE.magicInside.ingredients
          : (p.magic?.ingredients || []);
        const magicStops = orbitStopsForCount(magicIngredients.length);
        html = `
          <div class="home__orbit home__orbit--magic-list">
            ${renderOrbitGuide()}
            <ul class="home__magic-list home__orbit-list" aria-label="${magicTitle}">
              ${magicIngredients.map((item, index) => renderMagicRow(item, index, magicStops)).join('')}
            </ul>
          </div>`;
        if (panelLegend) panelLegend.hidden = true;
      } else if (state.tab === 'deal') {
        const dealText = window.DELI_DELE_SITE?.dealOfTheDay?.text || p.deal?.body || '';
        const dealHref = window.DELI_DELE_SITE?.dealOfTheDay?.url || p.deal?.href || site.routes?.retailCatalog || site.routes?.root || '/';
        html = `
          <div class="home__orbit home__orbit--story home__orbit--deal">
            ${renderOrbitGuide()}
            <article class="home__deal home__deal--promo home__orbit-item" style="${orbitStyle(0.5)}">
              <p class="home__deal-text">${dealText}</p>
              <a class="home__deal-pill-cta" href="${dealHref}" aria-label="${strings.unlockDeal || ''}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </a>
            </article>
          </div>`;
        if (panelLegend) panelLegend.hidden = true;
      }
      panel.innerHTML = html;
      requestAnimationFrame(() => panel.classList.remove('is-swapping'));
    }, 220);
  }

  function renderTabs() {
    tabButtons.forEach(btn => {
      const active = btn.dataset.tab === state.tab;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', String(active));
    });
    if (nodeText) nodeText.textContent = tabNodeLabels[state.tab] || '';
  }

  function renderFlavorSelection() {
    const p = getProduct();
    if (flavorLabel) flavorLabel.textContent = p.name;
    flavorList.querySelectorAll('[data-flavor-option]').forEach(btn => {
      btn.setAttribute('aria-selected', String(btn.dataset.flavorOption === state.productId));
    });
  }

  /* ---------- Event handlers ---------- */
  function setTab(tabId) {
    if (state.tab === tabId) return;
    state.tab = tabId;
    renderTabs();
    renderPanel();
  }

  function setProduct(id) {
    if (state.productId === id) return;
    state.productId = id;
    renderProduct();
    renderPanel();
    renderFlavorSelection();
  }

  function openFlavor() {
    flavor.classList.add('is-open');
    flavorToggle.setAttribute('aria-expanded', 'true');
  }
  function closeFlavor() {
    flavor.classList.remove('is-open');
    flavorToggle.setAttribute('aria-expanded', 'false');
  }
  function toggleFlavor() {
    flavor.classList.contains('is-open') ? closeFlavor() : openFlavor();
  }

  function openMenu() {
    lastMenuFocus = document.activeElement instanceof HTMLElement ? document.activeElement : menuToggle;
    menuOverlay.hidden = false;
    requestAnimationFrame(() => {
      menuOverlay.classList.add('is-open');
      menuClose.focus();
    });
    menuToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }
  function closeMenu() {
    if (menuOverlay.hidden) return;
    menuOverlay.classList.remove('is-open');
    menuToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!menuOverlay.classList.contains('is-open')) {
        menuOverlay.hidden = true;
        (lastMenuFocus || menuToggle).focus();
      }
    }, 350);
  }

  /* ---------- Wire up ---------- */
  function bind() {
    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => setTab(btn.dataset.tab));
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab(btn.dataset.tab); }
      });
    });

    flavorToggle.addEventListener('click', (e) => { e.stopPropagation(); toggleFlavor(); });
    flavorToggle.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        openFlavor();
        flavorList.querySelector('[data-flavor-option]')?.focus();
      }
    });
    flavorList.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-flavor-option]');
      if (!opt) return;
      setProduct(opt.dataset.flavorOption);
      closeFlavor();
    });
    flavorList.addEventListener('keydown', (e) => {
      const options = Array.from(flavorList.querySelectorAll('[data-flavor-option]'));
      const currentIndex = options.indexOf(document.activeElement);
      if (e.key === 'Escape') {
        closeFlavor();
        flavorToggle.focus();
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        options[(currentIndex + 1) % options.length]?.focus();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        options[(currentIndex - 1 + options.length) % options.length]?.focus();
      }
      if ((e.key === 'Enter' || e.key === ' ') && document.activeElement?.dataset.flavorOption) {
        e.preventDefault();
        setProduct(document.activeElement.dataset.flavorOption);
        closeFlavor();
        flavorToggle.focus();
      }
    });
    document.addEventListener('click', (e) => {
      if (!flavor.contains(e.target)) closeFlavor();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { closeFlavor(); closeMenu(); }
    });

    menuToggle.addEventListener('click', openMenu);
    menuClose.addEventListener('click', closeMenu);
    menuOverlay.addEventListener('click', (e) => {
      if (e.target === menuOverlay) closeMenu();
    });
    menuOverlay.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(menuOverlay.querySelectorAll(menuFocusableSelector)).filter(element => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------- Futuristic Typing Animation for Hero ---------- */
  function initHeroTyping() {
    const heroEyebrow = document.querySelector('.home__hero-eyebrow');
    const heroCopy = heroEyebrow?.querySelector('[data-hero-copy]');
    const heroLinkCopy = heroEyebrow?.querySelector('[data-hero-link-copy]');
    if (!heroEyebrow || !heroCopy || !heroLinkCopy) return;

    const text = heroCopy.textContent.trim();
    const linkText = heroLinkCopy.textContent.trim();
    if (!text || !linkText) return;

    // Keep the signup link intact while its label types in after the invitation.
    heroCopy.textContent = '';
    heroLinkCopy.textContent = '';
    heroEyebrow.classList.add('home__hero-eyebrow--typing');

    let i = 0;
    const speed = 70; // typing speed in milliseconds

    function typeHeroLink() {
      let linkIndex = 0;

      function typeLinkLabel() {
        if (linkIndex < linkText.length) {
          heroLinkCopy.textContent += linkText.charAt(linkIndex);
          linkIndex++;
          setTimeout(typeLinkLabel, speed);
        } else {
          heroEyebrow.classList.remove('home__hero-eyebrow--typing');
        }
      }

      typeLinkLabel();
    }

    function typeWriter() {
      if (i < text.length) {
        heroCopy.textContent += text.charAt(i);
        i++;
        setTimeout(typeWriter, speed);
      } else {
        typeHeroLink();
      }
    }

    // Small delay before starting for dynamic entrance
    setTimeout(typeWriter, 500);
  }

  /* ---------- Keep the mobile legend aligned with Shopify Chat ---------- */
  function initChatOffsetSync() {
    const root = document.documentElement;
    let shadowObserver = null;
    let observedShadowRoot = null;
    const syncDelays = [0, 250, 1000, 2500, 5000, 8000];

    function syncChatOffset() {
      if (!window.matchMedia('(max-width: 520px)').matches) {
        root.style.removeProperty('--home-chat-bottom-offset');
        return;
      }

      const chatHost = document.querySelector('shopify-chat');
      const chatButton = chatHost?.shadowRoot?.querySelector(
        'button[aria-label="Open chat"], button.activator'
      );
      if (!chatButton) return;

      const rect = chatButton.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const bottomOffset = Math.max(16, Math.round(window.innerHeight - rect.bottom));
      root.style.setProperty('--home-chat-bottom-offset', `${bottomOffset}px`);
    }

    function scheduleChatOffsetSync() {
      syncDelays.forEach(delay => window.setTimeout(syncChatOffset, delay));
    }

    function observeChat() {
      const shadowRoot = document.querySelector('shopify-chat')?.shadowRoot;
      if (!shadowRoot || shadowRoot === observedShadowRoot) return false;

      shadowObserver?.disconnect();
      observedShadowRoot = shadowRoot;
      shadowObserver = new MutationObserver(syncChatOffset);
      shadowObserver.observe(shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'style']
      });
      scheduleChatOffsetSync();
      return true;
    }

    if (!observeChat()) {
      const hostObserver = new MutationObserver(() => {
        if (observeChat()) hostObserver.disconnect();
      });
      hostObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    window.addEventListener('resize', syncChatOffset, { passive: true });
    window.addEventListener('orientationchange', syncChatOffset, { passive: true });
    window.addEventListener('pageshow', scheduleChatOffsetSync, { passive: true });
  }

  /* ---------- Init ---------- */
  function init() {
    buildParticles();
    renderFlavorList();
    renderFlavorSelection();
    renderProduct();
    renderTabs();
    renderPanel();
    bind();
    initHeroTyping();
    initChatOffsetSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
